/**
 * SlackConfig Component - Modern Slack Integration Configuration
 *
 * This component provides a wizard-based UI for configuring Slack message nodes
 * using the new Slack Web API (bot token) integration instead of webhooks.
 *
 * === NEW FEATURES ===
 * - Bot token authentication with AWS Secrets Manager
 * - Multi-channel message delivery
 * - Advanced threading support (reply to thread, start new thread)
 * - Real-time message preview with sample data
 * - Block Kit support with JSON editor
 * - VariablePicker integration for dynamic content
 * - Comprehensive output configuration
 *
 * === MIGRATION FROM WEBHOOK ===
 * This component replaces the old webhook-based Slack integration with a modern
 * Web API approach that provides more features and better reliability.
 */

import { SlackWizard } from '../../features/integrations/slack/SlackWizard';
import { IntegrationsNodeData } from '../../types/nodes';

interface SlackConfigProps {
	nodeId: string;
	data: IntegrationsNodeData;
	onChange: (data: IntegrationsNodeData) => void;
	// Wizard state callbacks for parent component
	onWizardStateChange?: (state: {
		wizardMode: boolean;
		currentStep: number;
		totalSteps: number;
		canProceedToNext: boolean;
		onPreviousStep: () => void;
		onNextStep: () => void;
		onCompleteWizard: () => Promise<void>;
		validationAttempted: boolean;
		validationFailed: boolean;
	}) => void;
	onValidationRequest?: (validate: () => Promise<boolean>) => void;
	onClose?: () => void;
}

/**
 * Slack configuration component using the new wizard-based approach
 *
 * This component serves as a wrapper around the SlackWizard, providing
 * compatibility with the existing node configuration interface while
 * delivering the new Slack Web API functionality.
 */
export function SlackConfig({
	nodeId,
	data,
	onChange,
	onWizardStateChange,
	onValidationRequest,
	onClose,
}: SlackConfigProps) {
	return (
		<SlackWizard
			nodeId={nodeId}
			data={data}
			onChange={onChange}
			onWizardStateChange={onWizardStateChange}
			onValidationRequest={onValidationRequest}
			onClose={onClose}
		/>
	);
}
